import React from "react";
import { NodeOrEdge } from "@hex-insights/app-modules";
import {
	Button,
	ChildrenProps,
	ClassNameProps,
	Column,
	Conditional,
	doNothing,
	Else,
	Heading,
	HeadingLevel,
	If,
	makeClassName,
	Modal,
	Row,
	StyleProps,
	toGrammaticalNumber,
} from "@hex-insights/core";
import { Tile } from "../../../../Components/Tile";
import {
	asNode,
	BillingAdjustment,
	ContractInstallment,
	ContractLineItem,
	ContractLineItemPricingFragment,
	PaymentLineItem,
	Student,
} from "../../../../Utilities";
import { ContractInstallmentFormat, ModelFormat } from "../../../Format";
import { ContractInstallmentUtilities, ContractUtilities, ModelUtilities } from "../../../Utilities";
import { paymentStatusColors, PaymentStatusIcon } from "../../_General";
import { ContractInstallmentsListModal, ContractInstallmentsListModalProps } from "../../ContractInstallment";
import styles from "./styles.module.css";

export type ContractDueSummaryTileProps = {
	headingLevel?: HeadingLevel;
	variant: "admin" | "parent";
	studentID?: Student["id"];
	isLoading?: boolean;
	contracts:
		| NodeOrEdge<{
				contractLineItems: (Pick<ContractLineItem, "id"> & ContractLineItemPricingFragment)[];
				contractInstallments: (Pick<ContractInstallment, "id" | "dueDate" | "portion"> & {
					invoices: {
						paymentLineItems: Pick<PaymentLineItem, "amount">[];
						billingAdjustments: Pick<BillingAdjustment, "amount">[];
					}[];
				})[];
		  }>[]
		| null
		| undefined;
} & Partial<ClassNameProps & StyleProps>;

export function ContractDueSummaryTile({
	headingLevel = 3,
	variant,
	studentID,
	contracts,
	className,
	style,
}: ContractDueSummaryTileProps) {
	const {
		totalDue,
		isOverdue,
		dueContractInstallments,
		overdueContractInstallments,
		upcomingContractInstallments,
		nextContractInstallment,
		overviewContractInstallments,
	} = React.useMemo(() => {
		if (!contracts) {
			return {
				totalDue: null,
				isOverdue: false,
				dueContractInstallments: [],
				overdueContractInstallments: [],
				upcomingContractInstallments: [],
				nextContractInstallment: null,
				overviewContractInstallments: [],
			};
		}

		const precomputedContractInstallments = ContractUtilities.precomputeContractInstallments(contracts);
		const contractInstallmentGroups = ContractInstallmentUtilities.groupContractInstallmentsByPaymentStatus(
			precomputedContractInstallments,
		);

		const paymentProgresses = contracts.map((contract) => ContractUtilities.calculatePaymentProgress(asNode(contract)));
		const paymentProgress = ContractUtilities.mergePaymentProgresses(paymentProgresses);

		const upcomingContractInstallments = contractInstallmentGroups[ModelUtilities.PaymentStatus.Upcoming];
		const nextContractInstallment = upcomingContractInstallments[0] ?? null;

		const dueContractInstallments = contractInstallmentGroups[ModelUtilities.PaymentStatus.Due];
		const overdueContractInstallments = contractInstallmentGroups[ModelUtilities.PaymentStatus.Overdue];
		let overviewContractInstallments = [...dueContractInstallments, ...overdueContractInstallments];
		if (overdueContractInstallments.length === 0) {
			overviewContractInstallments = upcomingContractInstallments;
		}

		return {
			totalDue: paymentProgress.amountDue + paymentProgress.amountOverdue,
			isOverdue: paymentProgress.amountOverdue > 0,
			dueContractInstallments,
			overdueContractInstallments,
			upcomingContractInstallments,
			nextContractInstallment,
			overviewContractInstallments,
		};
	}, [contracts]);

	const hasOverdueContractInstallments = overdueContractInstallments.length > 0;
	const hasDueContractInstallments = dueContractInstallments.length > 0;
	const hasUpcomingContractInstallments = upcomingContractInstallments.length > 0;

	let overdueText = "";
	let dueText = "";
	if (variant === "parent") {
		overdueText = `You have ${toGrammaticalNumber("overdue payment", overdueContractInstallments.length, true)}`;
		dueText = `You have ${toGrammaticalNumber("payment", dueContractInstallments.length, true)} due`;
	} else {
		overdueText = `${toGrammaticalNumber("overdue payment", overdueContractInstallments.length, true)}`;
		dueText = `${toGrammaticalNumber("payment", dueContractInstallments.length, true)} due`;
	}

	return (
		<Tile className={className} style={style}>
			<Tile.Header>
				<Row justify="space-between">
					<Heading level={headingLevel} noMargin>
						Total Due Today:
					</Heading>

					<OverviewContractInstallmentsButton
						variant={variant}
						studentID={studentID}
						contractInstallments={overviewContractInstallments}
					/>
				</Row>
			</Tile.Header>
			<Tile.Body>
				<Column justify="spaced-start" verticalSpacing="1rem">
					<span className={makeClassName(styles["total-due"], isOverdue ? styles["total-due--overdue"] : "")}>
						{ModelFormat.money(totalDue)}
					</span>

					<Column justify="spaced-start" verticalSpacing="0.5rem">
						<Conditional>
							<If condition={hasOverdueContractInstallments || hasDueContractInstallments}>
								<If condition={!(hasOverdueContractInstallments && hasDueContractInstallments)}>
									<ContractInstallmentsFillerButton />
								</If>
								<If condition={hasOverdueContractInstallments}>
									<ContractInstallmentsButton
										variant={variant}
										studentID={studentID}
										paymentStatus={ModelUtilities.PaymentStatus.Overdue}
										contractInstallments={overdueContractInstallments}
									>
										{overdueText}
									</ContractInstallmentsButton>
								</If>
								<If condition={hasDueContractInstallments}>
									<ContractInstallmentsButton
										variant={variant}
										studentID={studentID}
										paymentStatus={ModelUtilities.PaymentStatus.Due}
										contractInstallments={dueContractInstallments}
									>
										{dueText}
									</ContractInstallmentsButton>
								</If>
							</If>
							<If condition={hasUpcomingContractInstallments}>
								<ContractInstallmentsFillerButton />
								<ContractInstallmentsButton
									variant={variant}
									studentID={studentID}
									paymentStatus={ModelUtilities.PaymentStatus.Upcoming}
									contractInstallments={upcomingContractInstallments}
								>
									<Row justify="space-between" style={{ flexGrow: 1 }}>
										<span>Next Payment Due:</span>
										<span>
											{nextContractInstallment &&
												ContractInstallmentFormat.Fields.dueDate(nextContractInstallment.dueDate)}
										</span>
									</Row>
								</ContractInstallmentsButton>
							</If>
							<Else>
								<ContractInstallmentsFillerButton />
								<ContractInstallmentsFillerButton />
							</Else>
						</Conditional>
					</Column>
				</Column>
			</Tile.Body>
		</Tile>
	);
}

type OverviewContractInstallmentsButtonProps = {
	variant: "admin" | "parent";
	studentID?: Student["id"];
	contractInstallments: ContractInstallmentsListModalProps["contractInstallments"];
};

function OverviewContractInstallmentsButton({
	variant,
	studentID,
	contractInstallments,
}: OverviewContractInstallmentsButtonProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	if (contractInstallments.length === 0) {
		return null;
	}

	return (
		<React.Fragment>
			<Button variant="link" size="small" onClick={openModal}>
				View
			</Button>

			<Modal.If condition={isModalOpen}>
				<ContractInstallmentsListModal
					variant={variant}
					studentID={studentID}
					contractInstallments={contractInstallments}
					onClose={closeModal}
				/>
			</Modal.If>
		</React.Fragment>
	);
}

type ContractInstallmentsButtonProps = {
	variant: "admin" | "parent";
	studentID?: Student["id"];
	paymentStatus: ModelUtilities.PaymentStatus;
	contractInstallments: ContractInstallmentsListModalProps["contractInstallments"];
} & Partial<ChildrenProps>;

function ContractInstallmentsButton({
	variant,
	studentID,
	paymentStatus,
	contractInstallments,
	children,
}: ContractInstallmentsButtonProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	let colors = paymentStatusColors[paymentStatus];
	if (paymentStatus === ModelUtilities.PaymentStatus.Upcoming) {
		colors = {
			color: "var(--brand-colors--primary--dark)",
			backgroundColor: "var(--brand-colors--primary--light)",
			iconColor: "var(--brand-colors--primary--dark)",
		};
	}

	return (
		<React.Fragment>
			<Button
				onClick={openModal}
				className={styles["contract-installments-button"]}
				style={{ "--color": colors.color, "--background-color": colors.backgroundColor } as React.CSSProperties}
			>
				<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
					<PaymentStatusIcon status={paymentStatus} size={22} color={colors.color} />
					{children}
				</Row>
			</Button>

			<Modal.If condition={isModalOpen}>
				<ContractInstallmentsListModal
					variant={variant}
					studentID={studentID}
					contractInstallments={contractInstallments}
					onClose={closeModal}
				/>
			</Modal.If>
		</React.Fragment>
	);
}

function ContractInstallmentsFillerButton() {
	return (
		<Button
			onClick={doNothing}
			className={styles["contract-installments-button"] + " " + styles["contract-installments-button--filler"]}
			disabled
		>
			<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
				<PaymentStatusIcon status={ModelUtilities.PaymentStatus.Upcoming} size={22} />
				No payments to display
			</Row>
		</Button>
	);
}
